import { useEffect, useRef } from 'react';
import { noop } from 'lodash';

/**
 * A hook which enables us to track animation iteration events.
 *
 * @example
 *  const { ref } = useAnimationIteration({
 *    isIterating: isPulsing,
 *    onIteration: onPulseIteration,
 *  });
 */
export type UseAnimationIterationOptions = {
  /** True if the pulse is running */
  isIterating: boolean;
  /** The pulse has finished animating to its smallest size */
  onIteration: () => void;
};

export function useAnimationIteration({
  isIterating,
  onIteration,
}: UseAnimationIterationOptions) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (isIterating) {
      const el = containerRef.current! as HTMLElement;
      el.addEventListener('animationiteration', onIteration);

      return () => {
        el.removeEventListener('animationiteration', onIteration);
      };
    }

    return noop;
  }, [isIterating, onIteration]);

  return { ref: containerRef };
}
